import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import BeatCard from '../beats/BeatCard';
import VerseCard from '../verses/VerseCard';
import ProfileEditModal from './ProfileEditModal';
import ConfirmDeleteModal from '../beats/ConfirmDeleteModal';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuth } from '../../context/AuthContext';
import { isArtistRole } from '../../types/chat';
import { formatInstagramUrl, formatYouTubeUrl } from '../../utils/profileSocialLinks';
import './UserProfile.css';

type ContentTab = 'beats' | 'verses' | 'shows';

interface HeroTab {
  id: ContentTab;
  label: string;
  count?: number;
}

const UserProfile: React.FC = () => {
  const {
    profile,
    beats,
    verses,
    loading,
    error,
    isModalOpen,
    setIsModalOpen,
    editLoading,
    editError,
    editSuccess,
    handleEditSubmit,
    isOwnProfile,
    showBeats,
    showVerses,
    showShows,
    formatRoleLabel,
    removeVerse,
  } = useUserProfile();

  const { authState } = useAuth();
  const [activeTab, setActiveTab] = useState<ContentTab>('beats');
  const [heroReady, setHeroReady] = useState(false);
  const [pendingVerseId, setPendingVerseId] = useState<string | null>(null);
  const [deletingVerse, setDeletingVerse] = useState(false);
  const [verseDeleteError, setVerseDeleteError] = useState<string | null>(null);

  const confirmVerseDelete = async () => {
    if (!pendingVerseId) return;
    setDeletingVerse(true);
    setVerseDeleteError(null);
    try {
      await removeVerse(pendingVerseId);
      setPendingVerseId(null);
    } catch (err) {
      setVerseDeleteError(err instanceof Error ? err.message : 'Could not delete that verse');
    } finally {
      setDeletingVerse(false);
    }
  };

  // DMs are artist-to-artist only.
  const canMessage =
    !isOwnProfile &&
    isArtistRole(authState.user?.role) &&
    isArtistRole(profile?.role);

  const heroTabs = useMemo((): HeroTab[] => {
    const tabs: HeroTab[] = [];
    if (showBeats) tabs.push({ id: 'beats', label: 'Beats', count: beats.length });
    if (showVerses) tabs.push({ id: 'verses', label: 'Verses', count: verses.length });
    if (showShows) tabs.push({ id: 'shows', label: 'Shows / Gigs' });
    return tabs;
  }, [showBeats, showVerses, showShows, beats.length, verses.length]);

  useEffect(() => {
    if (!loading && profile) {
      const t = requestAnimationFrame(() => setHeroReady(true));
      return () => cancelAnimationFrame(t);
    }
    setHeroReady(false);
  }, [loading, profile]);

  useEffect(() => {
    if (showBeats) {
      setActiveTab('beats');
    } else if (showVerses) {
      setActiveTab('verses');
    } else if (showShows) {
      setActiveTab('shows');
    }
  }, [showBeats, showVerses, showShows, profile?.id]);

  useEffect(() => {
    const ids = heroTabs.map(t => t.id);
    if (ids.length === 0) return;
    if (!ids.includes(activeTab)) {
      setActiveTab(ids[0]);
    }
  }, [heroTabs, activeTab]);

  if (loading) {
    return (
      <div className="profile-preview profile-preview--loading">
        <div className="profile-preview__spinner" aria-label="Loading" />
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-preview profile-preview--error">
        <p>{error || 'Profile not found'}</p>
        <Link to="/home" className="profile-preview__link-back">
          Back to Home
        </Link>
      </div>
    );
  }

  const instagramUrl = profile.instagram ? formatInstagramUrl(profile.instagram) : '';
  const youtubeUrl = profile.youtube ? formatYouTubeUrl(profile.youtube) : '';
  const memberSince = new Date(profile.created_at).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="profile-preview">
      <Helmet>
        <title>DHH Collab | {profile.username}</title>
        <meta name="description" content={`${profile.username} on DHH Collab — beats, verses, and collabs.`} />
      </Helmet>

      <section className={`profile-preview__hero ${heroReady ? 'profile-preview__hero--visible' : ''}`}>
        <div className="profile-preview__hero-bg" aria-hidden>
          <span className="profile-preview__blob profile-preview__blob--1" />
          <span className="profile-preview__blob profile-preview__blob--2" />
          <span className="profile-preview__wave-lines" />
        </div>

        <div className="profile-preview__hero-inner max-w-6xl mx-auto container-padding">
          <div className="profile-preview__hero-card">
            <div className="profile-preview__avatar-wrap">
              <span className="profile-preview__avatar-ring" aria-hidden />
              <span className="profile-preview__avatar-ring profile-preview__avatar-ring--delay" aria-hidden />
              <div className="profile-preview__avatar">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt="" />
                ) : (
                  <span>{profile.username?.[0]?.toUpperCase() || 'U'}</span>
                )}
              </div>
              <span className="profile-preview__note profile-preview__note--1" aria-hidden>
                ♪
              </span>
              <span className="profile-preview__note profile-preview__note--2" aria-hidden>
                ♫
              </span>
              <span className="profile-preview__note profile-preview__note--3" aria-hidden>
                ♪
              </span>
            </div>

            <div className="profile-preview__identity">
              <div className="profile-preview__identity-top">
                <div>
                  <span className="profile-preview__role-badge">{formatRoleLabel(profile.role)}</span>
                  <h1 className="profile-preview__username">{profile.username}</h1>
                  {profile.state && <p className="profile-preview__location">{profile.state}</p>}
                  <p className="profile-preview__since">Member since {memberSince}</p>
                </div>

                {isOwnProfile && (
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="profile-preview__edit-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="profile-preview__edit-label">Edit</span>
                  </button>
                )}

                {canMessage && (
                  <Link
                    to={`/messages?to=${profile.id}`}
                    className="profile-preview__message-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 10h8M8 14h5m7-2a8 8 0 01-8 8H8l-4 3v-4.5A8 8 0 1120 12z"
                      />
                    </svg>
                    <span>Message</span>
                  </Link>
                )}
              </div>

              {profile.bio && <p className="profile-preview__bio">{profile.bio}</p>}

              <div className="profile-preview__social">
                {instagramUrl && (
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="profile-preview__social-link profile-preview__social-link--ig"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M7.75 2h8.5A5.75 5.75 0 0122 7.75v8.5A5.75 5.75 0 0116.25 22h-8.5A5.75 5.75 0 012 7.75v-8.5A5.75 5.75 0 017.75 2zm0 1.5A4.25 4.25 0 003.5 7.75v8.5A4.25 4.25 0 007.75 20.5h8.5a4.25 4.25 0 004.25-4.25v-8.5A4.25 4.25 0 0016.25 3.5zm4.75 2.25a.75.75 0 111.5 0 .75.75 0 01-1.5 0zM12 7.25A4.75 4.75 0 117.25 12 4.75 4.75 0 0112 7.25zm0 1.5A3.25 3.25 0 108.75 12 3.25 3.25 0 0112 8.75z" />
                    </svg>
                    Instagram
                  </a>
                )}
                {youtubeUrl && (
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="profile-preview__social-link profile-preview__social-link--yt"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M21.8 8.001a2.752 2.752 0 00-1.938-1.948C18.077 6 12 6 12 6s-6.077 0-7.862.053A2.752 2.752 0 002.2 8.001C2 9.785 2 12 2 12s0 2.215.2 3.999a2.752 2.752 0 001.938 1.948C5.923 18 12 18 12 18s6.077 0 7.862-.053a2.752 2.752 0 001.938-1.948C22 14.215 22 12 22 12s0-2.215-.2-3.999zM10 15.5v-7l6 3.5-6 3.5z" />
                    </svg>
                    YouTube
                  </a>
                )}
              </div>
            </div>
          </div>

          {heroTabs.length > 0 && (
            <div className="profile-preview__hero-tabs" role="tablist" aria-label="Profile content">
              {heroTabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`profile-tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`profile-panel-${tab.id}`}
                  className={`profile-preview__hero-tab ${activeTab === tab.id ? 'profile-preview__hero-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.count !== undefined && (
                    <span className="profile-preview__hero-tab-value">{tab.count}</span>
                  )}
                  <span className="profile-preview__hero-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {heroTabs.length > 0 && (
        <section className="profile-preview__body max-w-6xl mx-auto container-padding">
          <div
            key={activeTab}
            id={`profile-panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`profile-tab-${activeTab}`}
            className="profile-preview__panel"
          >
            {activeTab === 'beats' && showBeats && (
              <>
                {beats.length > 0 ? (
                  <div className="profile-preview__grid">
                    {beats.map(beat => (
                      <BeatCard key={beat.id} beat={beat} />
                    ))}
                  </div>
                ) : (
                  <div className="profile-preview__empty">No beats uploaded yet.</div>
                )}
              </>
            )}

            {activeTab === 'verses' && showVerses && (
              <>
                {verses.length > 0 ? (
                  <div className="profile-preview__grid">
                    {verses.map(verse => (
                      <VerseCard
                        key={verse.id}
                        verse={verse}
                        onDelete={isOwnProfile ? setPendingVerseId : undefined}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="profile-preview__empty">No verses uploaded yet.</div>
                )}
              </>
            )}

            {activeTab === 'shows' && showShows && (
              <div className="profile-preview__empty profile-preview__empty--shows">
                <span className="profile-preview__shows-icon" aria-hidden>
                  🎤
                </span>
                <h2 className="profile-preview__shows-title">Shows &amp; Gigs</h2>
                <p>Coming soon — list your live shows and gigs here.</p>
                <p className="profile-preview__empty-hint">This section will be available in a future update.</p>
              </div>
            )}
          </div>
        </section>
      )}

      <ProfileEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        profileData={profile}
        onSubmit={handleEditSubmit}
        isLoading={editLoading}
        error={editError}
        successMessage={editSuccess}
      />

      <ConfirmDeleteModal
        open={!!pendingVerseId}
        onClose={() => {
          setPendingVerseId(null);
          setVerseDeleteError(null);
        }}
        onConfirm={confirmVerseDelete}
        isDeleting={deletingVerse}
        title="Delete this verse?"
        message="Your recording will be removed for good."
        warning={verseDeleteError || 'This cannot be undone.'}
      />
    </div>
  );
};

export default UserProfile;
